import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";
import { set } from "date-fns";

// Create the EmploymentTypeMasterContext
const WorkplaceMasterContext = createContext();

// Custom hook to use the EmploymentTypeMasterContext
export const useWorkplaceMaster = () => useContext(WorkplaceMasterContext);

// Provider component to wrap the app
export const WorkplaceMasterProvider = ({ children }) => {
  const [workplaces, setWorkplace] = useState([]);
  const API_URL = "https://hrmsnew.prepstripe.com/api/masters/workplaces"; // Replace with your actual API URL
  const token = getToken();
  const [isLoading, setLoading] = useState(false);

  // Fetch workplace master from the API on load
  useEffect(() => {
    const fetchWorkplace = async () => {
      try {
        setLoading(true);
        const response = await axios.get(API_URL, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setWorkplace(response.data?.data || {}); // Access `data.data` for actual workplace master
        setLoading(false);
      } catch (error) {
        console.error("Error fetching workplace master:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkplace();
  }, [API_URL, token]);

  // Add a new workplace
  const addWorkplace = async (newWorkplace) => {
    try {
      const response = await axios.post(API_URL, newWorkplace, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setWorkplace((prev) => [response.data?.data, ...prev]);
    } catch (error) {
      console.error("Error adding workplace:", error);
    }
  };

  // Update an existing workplace
  const updateWorkplace = async (updatedWorkplace) => {
    try {
      const response = await axios.put(`${API_URL}/${updatedWorkplace.id}`, updatedWorkplace, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setWorkplace((prev) =>
        prev.map((workplace) =>
            workplace.id === updatedWorkplace.id ? { ...workplace, ...response.data?.data } : workplace
        )
      );
    } catch (error) {
      console.error("Error updating workplace master:", error);
    }
  };

  // Delete a employment-type
  const deleteWorkplace = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setWorkplace((prev) => prev.filter((workplace) => workplace.id !== id));
    } catch (error) {
      console.error("Error deleting workplace:", error);
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
        isLoading,
        workplaces,
        addWorkplace,
        updateWorkplace,
        deleteWorkplace,
    }),
    [workplaces, isLoading] // Recalculate the value only when `workplace` changes
  );

  return <WorkplaceMasterContext.Provider value={value}>{children}</WorkplaceMasterContext.Provider>;
};
